import { MetaManager } from "@/components/seo/MetaManager";
import { MainNavigation } from "@/components/layout/MainNavigation";
import { Footer } from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { EnhancedFAQs } from "@/components/seo/EnhancedFAQs";
import { AuthorBio } from "@/components/content/AuthorBio";
import { RelatedResources } from "@/components/seo/RelatedResources";
import { BreadcrumbNavigation } from "@/components/seo/BreadcrumbNavigation";
import { CheckCircle, AlertTriangle, Users, Shield, DollarSign, Award } from "lucide-react";

const SELECTION_FAQS = [
  {
    question: "What's the most important factor when choosing a PEO?",
    answer: "The most critical factor is ensuring the PEO has proper ESAC accreditation and financial stability. Beyond that, look for industry expertise relevant to your business, transparent pricing, responsive customer service, and technology that integrates well with your existing systems."
  },
  {
    question: "How much does a PEO typically cost per employee?",
    answer: "PEO costs typically range from $50-200+ per employee per month, depending on services included, employee count, and risk factors. Most PEOs charge 2-12% of payroll. Get quotes from multiple providers as pricing varies significantly based on your specific needs."
  },
  {
    question: "What red flags should I watch for when evaluating PEOs?",
    answer: "Major red flags include: lack of ESAC accreditation, unwillingness to provide client references, unclear pricing structures, poor financial ratings, high client turnover, limited customer service hours, or pressure to sign immediately without proper due diligence time."
  },
  {
    question: "Should I choose a large national PEO or smaller regional one?",
    answer: "Both have advantages. Large national PEOs often offer more resources, better benefits, and advanced technology. Smaller regional PEOs may provide more personalized service and industry specialization. The best choice depends on your specific needs, growth plans, and service preferences."
  },
  {
    question: "How long does it take to implement a new PEO?",
    answer: "PEO implementation typically takes 30-90 days. The timeline depends on your company size, complexity of existing systems, payroll transition requirements, and benefit enrollment needs. Proper planning and dedicated project management help ensure smooth transitions."
  }
];

const RELATED_RESOURCES = [
  {
    title: "Best PEO Companies 2025",
    description: "Expert rankings and reviews of top PEO providers",
    url: "/best-peo-providers",
    type: "service-guide" as const
  },
  {
    title: "PEO Vetting Process",
    description: "How we evaluate and approve PEO partners",
    url: "/peo-vetting-process",
    type: "service-guide" as const
  },
  {
    title: "Northeast PEO Services",
    description: "Regional PEO expertise for Northeast businesses",
    url: "/regions/northeast",
    type: "state-guide" as const
  },
  {
    title: "Healthcare Industry PEOs",
    description: "Specialized PEO services for healthcare companies",
    url: "/industries/healthcare",
    type: "industry-guide" as const
  }
];

export const PEOSelectionGuide = () => {
  return (
    <div className="min-h-screen bg-background">
      <MetaManager
        title="Complete PEO Selection Guide: How to Choose the Right Professional Employer Organization"
        description="Expert guide to selecting the perfect PEO for your business. Learn the key factors, questions to ask, red flags to avoid, and step-by-step evaluation process for choosing Professional Employer Organizations."
        keywords="PEO selection guide, choose PEO, Professional Employer Organization selection, PEO evaluation, PEO comparison guide, how to select PEO"
        canonical="https://thepeoapp.com/peo-selection-guide"
      />
      
      <MainNavigation />
      
      <main className="pt-16">
        <div className="container mx-auto px-4 py-8">
          <BreadcrumbNavigation 
            items={[
              { label: "Home", href: "/" },
              { label: "Resources", href: "/resources" },
              { label: "PEO Selection Guide", href: "/peo-selection-guide" }
            ]}
          />
          
          <div className="max-w-4xl mx-auto">
            <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-6">
              Complete PEO Selection Guide: How to Choose the Right Professional Employer Organization
            </h1>
            
            <div className="prose prose-lg max-w-none mb-12 text-muted-foreground">
              <p className="text-xl leading-relaxed">
                Choosing the right Professional Employer Organization (PEO) is one of the most important decisions for your business's HR infrastructure. This comprehensive guide walks you through every step of the selection process to ensure you find the perfect PEO partner.
              </p>
            </div>

            <div className="bg-primary/5 p-8 rounded-xl mb-12">
              <h2 className="text-2xl font-bold mb-6 flex items-center">
                <CheckCircle className="h-6 w-6 text-primary mr-2" />
                Key Factors to Evaluate
              </h2>
              <div className="grid md:grid-cols-2 gap-8">
                <div className="space-y-6">
                  <div className="flex items-start space-x-3">
                    <Shield className="h-5 w-5 text-primary mt-1" />
                    <div>
                      <h3 className="font-semibold mb-2">ESAC Accreditation & Financial Stability</h3>
                      <p className="text-sm text-muted-foreground">
                        Verify ESAC accreditation and review financial ratings from agencies like A.M. Best or S&P. This ensures your PEO can meet long-term obligations.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <Users className="h-5 w-5 text-primary mt-1" />
                    <div>
                      <h3 className="font-semibold mb-2">Industry Expertise</h3>
                      <p className="text-sm text-muted-foreground">
                        Choose PEOs with deep experience in your industry. They'll understand your specific compliance needs and common challenges.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <DollarSign className="h-5 w-5 text-primary mt-1" />
                    <div>
                      <h3 className="font-semibold mb-2">Transparent Pricing</h3>
                      <p className="text-sm text-muted-foreground">
                        Demand clear, detailed pricing with no hidden fees. Understand what's included and what costs extra.
                      </p>
                    </div>
                  </div>
                </div>
                <div className="space-y-6">
                  <div className="flex items-start space-x-3">
                    <Award className="h-5 w-5 text-primary mt-1" />
                    <div>
                      <h3 className="font-semibold mb-2">Service Quality</h3>
                      <p className="text-sm text-muted-foreground">
                        Evaluate customer service responsiveness, dedicated account management, and implementation support quality.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="h-5 w-5 text-primary mt-1" />
                    <div>
                      <h3 className="font-semibold mb-2">Technology Platform</h3>
                      <p className="text-sm text-muted-foreground">
                        Assess the user experience, mobile access, integration capabilities, and reporting features of their platform.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <Users className="h-5 w-5 text-primary mt-1" />
                    <div>
                      <h3 className="font-semibold mb-2">Client References</h3>
                      <p className="text-sm text-muted-foreground">
                        Speak with current clients in similar industries and company sizes to get real-world feedback.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-card p-8 rounded-xl shadow-lg mb-12">
              <h2 className="text-2xl font-bold mb-6">Step-by-Step Selection Process</h2>
              <div className="space-y-6">
                <div className="border-l-4 border-primary pl-6">
                  <h3 className="font-semibold text-lg mb-2">1. Define Your Requirements</h3>
                  <p className="text-muted-foreground">
                    List your specific needs: services required, employee count, locations, industry requirements, budget range, and growth plans. This becomes your evaluation criteria.
                  </p>
                </div>
                <div className="border-l-4 border-primary pl-6">
                  <h3 className="font-semibold text-lg mb-2">2. Research and Shortlist</h3>
                  <p className="text-muted-foreground">
                    Create a shortlist of 3-5 PEOs that meet your basic criteria. Use resources like <Link to="/best-peo-providers" className="text-primary hover:underline">our PEO rankings</Link> and industry recommendations.
                  </p>
                </div>
                <div className="border-l-4 border-primary pl-6">
                  <h3 className="font-semibold text-lg mb-2">3. Request Detailed Proposals</h3>
                  <p className="text-muted-foreground">
                    Get comprehensive proposals including detailed pricing, service descriptions, implementation timelines, and contract terms. Ensure all proposals cover the same scope.
                  </p>
                </div>
                <div className="border-l-4 border-primary pl-6">
                  <h3 className="font-semibold text-lg mb-2">4. Conduct Due Diligence</h3>
                  <p className="text-muted-foreground">
                    Verify ESAC accreditation, check financial ratings, review client references, and assess their technology platform through demos.
                  </p>
                </div>
                <div className="border-l-4 border-primary pl-6">
                  <h3 className="font-semibold text-lg mb-2">5. Negotiate and Decide</h3>
                  <p className="text-muted-foreground">
                    Compare final proposals, negotiate terms, and select the PEO that best aligns with your business needs and budget. Don't rush this decision.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-destructive/5 border border-destructive/20 p-8 rounded-xl mb-12">
              <h2 className="text-2xl font-bold mb-6 flex items-center text-destructive">
                <AlertTriangle className="h-6 w-6 mr-2" />
                Critical Red Flags to Avoid
              </h2>
              <div className="grid md:grid-cols-2 gap-6">
                <ul className="space-y-3 text-sm">
                  <li className="flex items-start space-x-2">
                    <span className="text-destructive">•</span>
                    <span>No ESAC accreditation or poor financial ratings</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <span className="text-destructive">•</span>
                    <span>Unwillingness to provide client references</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <span className="text-destructive">•</span>
                    <span>Vague or confusing pricing structures</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <span className="text-destructive">•</span>
                    <span>High-pressure sales tactics or rush to sign</span>
                  </li>
                </ul>
                <ul className="space-y-3 text-sm">
                  <li className="flex items-start space-x-2">
                    <span className="text-destructive">•</span>
                    <span>Limited customer service hours or responsiveness</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <span className="text-destructive">•</span>
                    <span>Outdated technology or poor user experience</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <span className="text-destructive">•</span>
                    <span>High client turnover or negative reviews</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <span className="text-destructive">•</span>
                    <span>Unclear contract terms or exit procedures</span>
                  </li>
                </ul>
              </div>
            </div>

            <div className="text-center mb-12 bg-gradient-to-r from-primary/10 to-secondary/10 p-8 rounded-xl">
              <h2 className="text-2xl font-bold mb-4">Ready to Find Your Perfect PEO?</h2>
              <p className="text-muted-foreground mb-6">
                Use our free matching service to get connected with pre-vetted PEOs that meet your specific requirements
              </p>
              <Link to="/application">
                <Button size="lg" className="text-lg px-8 py-6">
                  Get Free PEO Quotes
                </Button>
              </Link>
            </div>

            <RelatedResources 
              title="Related PEO Resources"
              resources={RELATED_RESOURCES}
            />

            <div className="mt-16">
              <EnhancedFAQs 
                title="PEO Selection FAQ"
                faqs={SELECTION_FAQS}
                showSchema={true}
              />
            </div>

            <div className="mt-16">
              <AuthorBio showFullBio={true} />
            </div>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};